import React from 'react';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { fmtCurrency, fmtDate } from './helpers';

const BillDetailDialog = ({ billDetail, setBillDetail, billDetailLoading }) => {
  return (
    <Dialog open={!!billDetail} onOpenChange={v => { if (!v) setBillDetail(null); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto z-50" data-testid="bill-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge className={billDetail?.type === 'SE' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}>{billDetail?.type}</Badge>
            {billDetail?.number}
          </DialogTitle>
          <DialogDescription>Bill Details</DialogDescription>
        </DialogHeader>
        {billDetailLoading ? (
          <div className="py-10 text-center text-gray-400">Loading...</div>
        ) : billDetail?.data ? (
          <div className="space-y-4 text-sm" data-testid="bill-detail-content">
            {/* Header Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Entry #', billDetail.data.entry_number || billDetail.data.return_number],
                ['Date', fmtDate(billDetail.data.entry_date || billDetail.data.return_date)],
                ['Invoice #', billDetail.data.invoice_number || billDetail.data.credit_note_number || '-'],
                ['Channel', (billDetail.data.channel || '-').replace(/_/g, ' ')],
                ['Customer', billDetail.data.customer_name || '-'],
                ['Total Amount', fmtCurrency(billDetail.data.total_amount || billDetail.data.total_value || 0)],
                ['Transport', billDetail.data.transport_name || '-'],
                ['Order Source', billDetail.data.order_source || '-'],
              ].map(([label, val]) => (
                <div key={label} className="bg-gray-50 rounded-lg p-2">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
                  <div className="font-medium text-gray-800 mt-0.5">{val}</div>
                </div>
              ))}
            </div>
            {/* Items Table */}
            {billDetail.data.items?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Items ({billDetail.data.items.length})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-100 border-b">
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-2 py-2 text-left">Size</th>
                      <th className="px-2 py-2 text-left">Color</th>
                      <th className="px-2 py-2 text-right">Qty</th>
                      <th className="px-2 py-2 text-right">Rate</th>
                      <th className="px-2 py-2 text-right text-amber-700">Disc %</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr></thead>
                    <tbody>
                      {(() => {
                        let grandTotal = 0;
                        const rows = billDetail.data.items.flatMap((item, i) =>
                          (item.variants || []).map((v, j) => {
                            const qty = v.quantity || v.qty || v.qty_returned || 0;
                            const rate = v.rate || v.price || v.wsp || 0;
                            const disc = v.discount_percent || v.discount_pct || 0;
                            const lineAmt = Math.round(qty * rate * (1 - disc / 100));
                            grandTotal += lineAmt;
                            return (
                              <tr key={`${i}-${j}`} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="px-3 py-1.5 font-medium">{j === 0 ? (item.product_name || item.design_code || '-') : ''}</td>
                                <td className="px-2 py-1.5">{v.size}</td>
                                <td className="px-2 py-1.5">{v.color}</td>
                                <td className="px-2 py-1.5 text-right">{qty}</td>
                                <td className="px-2 py-1.5 text-right">{rate > 0 ? fmtCurrency(rate) : '-'}</td>
                                <td className="px-2 py-1.5 text-right text-amber-700">{disc > 0 ? `${disc}%` : '-'}</td>
                                <td className="px-3 py-1.5 text-right font-medium">{fmtCurrency(lineAmt)}</td>
                              </tr>
                            );
                          })
                        );
                        return (
                          <>
                            {rows}
                            <tr className="bg-gray-100 border-t-2 border-gray-300">
                              <td colSpan={6} className="px-3 py-2 text-right font-bold text-gray-700">Total</td>
                              <td className="px-3 py-2 text-right font-bold text-gray-900">{fmtCurrency(grandTotal)}</td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
                {(billDetail.data.freight_charges || 0) > 0 && (
                  <div className="text-right text-xs mt-1 text-gray-500">Freight: {fmtCurrency(billDetail.data.freight_charges)}</div>
                )}
              </div>
            )}
            {billDetail.data.notes && (
              <div className="bg-yellow-50 p-3 rounded-lg text-xs">
                <span className="font-semibold">Notes:</span> {billDetail.data.notes}
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default BillDetailDialog;
